// Foundation shared by every Form route. See GLOSSARY.md for the vocabulary
// (Form route, Command intent, Field schema, Wire shape) and
// docs/adr/0004-form-routes-and-field-schemas.md for why it is shaped this way.
//
// This module is deliberately client-safe: no db, no `.server` import, nothing
// that cannot be bundled for the browser. That is not incidental tidiness — the
// whole point of a Field schema is that the SDR's browser and the server check
// against the *same* object, so everything on that path has to survive being
// shipped to the browser. The old server-only schemas were unshareable precisely
// because they sat next to database imports.
import { z } from "zod";

/**
 * Per-form hints telling the Wire-shape decoder which paths are not plain
 * strings. HTTP delivers every value as a string, with no lists, no nesting and
 * no absence; these hints are the one declarative statement per Form route that
 * recovers the Field schema's real types. Paths use the bracket notation
 * TanStack Form emits for nested and array fields (`people[0].name`), so the
 * encoding is dictated by the form library rather than invented here.
 *
 * A form whose every field is a plain string declares `{}` — see
 * `app/routes/login.form.ts`. Do not reach for a string-to-typed transform in
 * the Field schema instead: the schema validates the *values* object, so a
 * transform whose input is `string` and output is `boolean` fails against a real
 * boolean. Types are recovered here, once, on the wire.
 */
export interface DecodeHints {
  arrays?: string[];
  booleans?: string[];
  dates?: string[];
  files?: string[];
  numbers?: string[];
}

/**
 * The field path a path-less (root) schema issue lands on once TanStack Form has
 * mapped Standard Schema issues onto field paths. An issue with an empty path is
 * a statement about the form as a whole rather than about any one input.
 */
export const FORM_LEVEL_PATH = "";

/**
 * Build a form-level (whole-form) validation failure in the same representation
 * TanStack Form uses for Standard Schema errors: a path→issues map, keyed here on
 * the root path because the failure belongs to no single field.
 *
 * Some Form route failures are not field-shape errors and have no field to land
 * on: wrong credentials, a stale optimistic lock, a rejected upload. They are
 * discovered on the server *after* the Field schema has passed. Returning them
 * in the form's own error map — rather than as a bare `{ error }` beside it —
 * means the client renders form-level and field-level failures through one code
 * path, the message travels with the submitted values so nothing the SDR typed is
 * lost, and the form clears it on the next valid keystroke like any other server
 * error.
 */
export function formLevelError(message: string) {
  return { [FORM_LEVEL_PATH]: [{ message }] };
}

/** One renderable validation issue, the only part of a Standard Schema issue used here. */
interface FormIssue {
  message: string;
}

/**
 * The `{ form, fields }` wrapper TanStack Form builds for a Standard Schema failure
 * validated at form level (`transformFormIssues` in `@tanstack/form-core`), typed so
 * it can both be assigned to `errorMap.onServer` — whose declared type is the flat
 * field-path→issues map — and be read back for the form's `errors` list. The
 * library's own runtime value for `onServer` is this wrapper; only its type is the
 * narrower map, so the intersection is where that gap is absorbed.
 */
export type FieldLevelError = Record<string, FormIssue[]> & {
  form: Record<string, FormIssue[]>;
  fields: Record<string, FormIssue[]>;
};

/**
 * Build a validation failure that belongs to one named field, in the shape
 * TanStack Form uses for a Standard Schema form error: `{ form, fields }`, with both
 * halves being the same field-path→issues map — which is exactly what the Field
 * schema itself produces for a field issue.
 *
 * The `fields` half is the load-bearing one: it is what the client distributes onto
 * that field's own error map, so a failure only the server could discover — a name
 * already taken, which no client-side check can rule out without racing — renders on
 * the input responsible for it, in the same place and the same words a Field schema
 * rejection would. Compare `formLevelError` above: the same idea for a failure that
 * belongs to no field at all, which needs no wrapper because nothing distributes it.
 */
export function fieldLevelError(field: string, message: string): FieldLevelError {
  const issues = { [field]: [{ message }] };

  // SAFETY: the value is constructed here, one line up, out of a string field name
  // and a string message — so `{ form, fields }` with both halves being the same
  // path→issues map is not an assumption about it, it is what it is. The chain is
  // the library's type gap rather than ours: this object is byte-for-byte what
  // `transformFormIssues` returns, but `errorMap.onServer` is declared as the flat
  // map that wrapper wraps, and `FieldLevelError` is the intersection that lets one
  // value be both. Removing the chain breaks `rejectSubmission` in ~/lib/forms.server.
  return { form: issues, fields: issues } as unknown as FieldLevelError;
}

/**
 * One entry of a TanStack Form error list, read for the string it can render.
 *
 * The list is typed `unknown[]` by the library because either representation may
 * turn up in it: a bare string a validator pushed, or a Standard Schema issue whose
 * message is the renderable part. Both decode to the message; anything else — a
 * number, an issue with no message — decodes to nothing and is dropped rather than
 * interpolated into the page as `[object Object]`.
 */
const RenderableError = z.union([
  z.string(),
  z.object({ message: z.string() }).transform((issue) => issue.message),
]);

/** Pull renderable strings out of a TanStack Form error list. */
export function errorMessages(errors: readonly unknown[]): string[] {
  const messages: string[] = [];

  for (const error of errors) {
    const renderable = RenderableError.safeParse(error);

    if (renderable.success) messages.push(renderable.data);
  }

  return messages;
}

/**
 * A path→issues map, read as leniently as the form itself writes one. A key whose
 * value is not a list carries no issues rather than invalidating the whole map, so
 * one odd entry cannot take an unrelated field's message off the screen.
 */
const IssueMap = z.record(z.string(), z.array(z.unknown()).catch([]));

/**
 * The `{ form, fields }` wrapper, recognised by the half that matters. `fields` is
 * where the form puts the per-field issues once it has distributed the wrapper, and
 * a value carrying it is read through it rather than as a map in its own right.
 */
const DistributableError = z.object({ fields: IssueMap });

/**
 * A Form route's server failure, in whichever of the two representations the caller
 * happens to be holding: the `{ form, fields }` wrapper a Standard Schema validator
 * returns (what a Form route action hands back), or the bare path→issues map the
 * form settles on once that wrapper has been distributed onto the fields. Absent
 * when the submission has not failed, which is most of the time.
 */
export type ServerFormError = FieldLevelError | Record<string, FormIssue[]> | null | undefined;

/**
 * Read a server error map as field path → messages. Both representations above are
 * accepted; the root path (`FORM_LEVEL_PATH`) appears alongside the real fields when
 * the failure belongs to the whole form.
 */
export function fieldErrorMessages(onServerError: ServerFormError) {
  const byPath: Record<string, string[]> = {};
  const distributable = DistributableError.safeParse(onServerError);

  const issueMap = distributable.success
    ? distributable.data.fields
    : IssueMap.safeParse(onServerError).data;

  if (!issueMap) return byPath;

  for (const [path, issues] of Object.entries(issueMap)) {
    const messages = errorMessages(issues);

    if (messages.length > 0) byPath[path] = messages;
  }

  return byPath;
}

/** Read back the message put there by `formLevelError`, if any. */
export function readFormLevelError(onServerError: ServerFormError): string | undefined {
  return fieldErrorMessages(onServerError)[FORM_LEVEL_PATH]?.[0];
}

// ------------------------------------------------- Field schema building blocks

/**
 * Required free text: present, and not made entirely of whitespace.
 *
 * One message covers all three ways to leave it blank — the key was absent, the key
 * was there and empty, the key was there and held spaces. A no-JavaScript browser can
 * omit a field entirely and a person can hold down the spacebar; the SDR should not
 * learn three vocabularies for one mistake.
 *
 * WHY A REFINEMENT AND NOT `.min(1)`, AND NOT `.trim().min(1)`. This is the shared
 * fix for the defect the review found on the collateral form, and both wrong answers
 * are worth naming because both look right:
 *
 *   - `.min(1)` counts characters, and a space is a character. `"   "` passes, so the
 *     browser lets the submission through and the action accepts it. The action then
 *     trims before writing — every `…InputFrom` helper does — and an SDR who typed
 *     three spaces into a *required* description has it persisted as `""`. The field
 *     was required, the value stored is empty, and nothing anywhere reported a
 *     problem.
 *   - `.trim().min(1)` rejects `"   "` correctly and is still wrong here, because a
 *     Field schema is validation-only: `createServerValidate` hands the action the
 *     *decoded* values, not the schema's parsed output, so the trim is discarded on
 *     the server while appearing to work on the client. Writing one invites the
 *     assumption that the action receives trimmed text, which is the same class of
 *     bug one layer along. See "A Field schema is validation-only" in docs/contributing/forms/pattern.md.
 *
 * A refinement on `value.trim().length` asks the question without answering with a
 * new value: it reads the trimmed length and returns a verdict, leaving the value
 * exactly as it arrived. Transformation stays where it belongs, in the action.
 *
 * The returned schema is still a `ZodString`, so a form that also has a length ceiling
 * chains it as usual: `requiredText("Name is required.").max(200, "Too long.")`.
 */
export function requiredText(message: string) {
  return z.string({ error: message }).refine((value) => value.trim().length > 0, message);
}
